import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    ApexBankTransactionDTO,
    ApexMerchantTransactionDTO,
    ApexEcwPaymentDTO,
    ApexPaymentViewDTO,
    ApexEcwPaymentLogDTO
} from '../types';
import axiosApexInstance from '../auth/axiosApexInstance';

interface PaymentContextProps {
    bankTransactions: ApexBankTransactionDTO[];
    merchantTransactions: ApexMerchantTransactionDTO[];
    ecwPayments: ApexEcwPaymentDTO[];
    apexPaymentView: ApexPaymentViewDTO[];
    loading: boolean;
    error: string | null;
    loadAll: () => void;
    getEcwPayments: (filter?: { fromDate?: string }) => Promise<ApexEcwPaymentDTO[]>;
    getApexPaymentsView: (filter?: { fromDate?: string }) => Promise<ApexPaymentViewDTO[]>;
    saveBankTransaction: (tran: ApexBankTransactionDTO) => Promise<void>;
    reconcilePayment: (
        ecwpmtId: number,
        bankTranId?: number,
        macctTranId?: number,
        scanId?: number,
        spmtId?: number
    ) => Promise<void>;
    uploadMerchantTransactions: (rows: ApexMerchantTransactionDTO[]) => Promise<void>;
    uploadBankTransactions: (rows: ApexBankTransactionDTO[]) => Promise<void>;
    fetchEcwPaymentLogs: (ecwpmtId: number) => Promise<ApexEcwPaymentLogDTO[]>;
}

const PaymentContext = createContext<PaymentContextProps | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [bankTransactions, setBankTransactions] = useState<ApexBankTransactionDTO[]>([]);
    const [merchantTransactions, setMerchantTransactions] = useState<ApexMerchantTransactionDTO[]>([]);
    const [ecwPayments, setEcwPayments] = useState<ApexEcwPaymentDTO[]>([]);
    const [apexPaymentView, setApexPaymentView] = useState<ApexPaymentViewDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apiPrefix = '/task/pmt';

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [bankRes, merchRes, ecwRes] = await Promise.all([
                axiosApexInstance.get(`${apiPrefix}/bank`),
                axiosApexInstance.get(`${apiPrefix}/merchant`),
                axiosApexInstance.get(`${apiPrefix}/ecw`)
            ]);
            setBankTransactions(bankRes.data);
            setMerchantTransactions(merchRes.data);
            setEcwPayments(ecwRes.data);
            setError(null);
        } catch (err) {
            setError('Failed to load payment data');
        } finally {
            setLoading(false);
        }
    }, []);

    const getEcwPayments = async (filter?: { fromDate?: string }) => {
        const params = new URLSearchParams();
        if (filter?.fromDate) params.append('fromDate', filter.fromDate);
        const res = await axiosApexInstance.get(`${apiPrefix}/ecw?${params.toString()}`);
        setEcwPayments(res.data);
        return res.data;
    };

    const getApexPaymentsView = async (filter?: { fromDate?: string }) => {
        const params = new URLSearchParams();
        if (filter?.fromDate) params.append('fromDate', filter.fromDate);
        const res = await axiosApexInstance.get(`${apiPrefix}/apexview?${params.toString()}`);
        setApexPaymentView(res.data);
        return res.data;
    };

    const saveBankTransaction = async (tran: ApexBankTransactionDTO) => {
        await axiosApexInstance.post(`${apiPrefix}/savebanktran`, tran);
        await loadAll();
    };

    const uploadMerchantTransactions = async (rows: ApexMerchantTransactionDTO[]) => {
        await axiosApexInstance.post(`${apiPrefix}/upload-merchant`, rows);
        await loadAll();
    };

    const uploadBankTransactions = async (rows: ApexBankTransactionDTO[]) => {
        await axiosApexInstance.post(`${apiPrefix}/upload-bank`, rows);
        await loadAll();
    };

    const reconcilePayment = async (
        ecwpmtId: number,
        bankTranId?: number,
        macctTranId?: number,
        scanId?: number,
        spmtId?: number
    ) => {
        const params = new URLSearchParams();
        params.append('ecwpmtId', ecwpmtId.toString());
        if (bankTranId) params.append('bankTranId', bankTranId.toString());
        if (macctTranId) params.append('macctTranId', macctTranId.toString());
        if (scanId) params.append('scanId', scanId.toString());
        if (spmtId) params.append('spmtId', spmtId.toString());
        await axiosApexInstance.post(`${apiPrefix}/reconcile?${params.toString()}`);
        await loadAll();
    };

    const fetchEcwPaymentLogs = async (ecwpmtId: number) => {
        const res = await axiosApexInstance.get(`${apiPrefix}/log/${ecwpmtId}`);
        return res.data as ApexEcwPaymentLogDTO[];
    };

    return (
        <PaymentContext.Provider
            value={{
                bankTransactions,
                merchantTransactions,
                ecwPayments,
                apexPaymentView,
                loading,
                error,
                loadAll,
                getEcwPayments,
                getApexPaymentsView,
                saveBankTransaction,
                reconcilePayment,
                uploadMerchantTransactions,
                uploadBankTransactions,
                fetchEcwPaymentLogs,
            }}
        >
            {children}
        </PaymentContext.Provider>
    );
};

export const usePaymentContext = (): PaymentContextProps => {
    const context = useContext(PaymentContext);
    if (!context) throw new Error('usePaymentContext must be used within a PaymentProvider');
    return context;
};
